import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

import { Button, Panel } from '../design-system/index.js';

type Props = {
  children: ReactNode;
  layout?: 'application' | 'page';
  onReset: (() => void) | undefined;
};

type State = { error: Error | null };

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Song Composer interface failed to render.', error, info);
  }

  reset = (): void => {
    this.setState({ error: null });
    this.props.onReset?.();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;

    const Frame = this.props.layout === 'page' ? 'div' : 'main';
    return (
      <Frame
        className={
          this.props.layout === 'page'
            ? 'grid min-h-80 place-items-center py-8 text-ink'
            : 'grid min-h-svh place-items-center bg-bg px-5 text-ink'
        }
      >
        <Panel
          className="w-full max-w-lg rounded-xl border border-border bg-surface p-6"
          aria-labelledby="app-error-title"
        >
          <AlertTriangle className="mb-5 size-6 text-err" aria-hidden="true" />
          <h1 id="app-error-title" className="text-2xl font-semibold tracking-tight">
            We couldn’t load this page.
          </h1>
          <p className="mt-3 text-sm leading-6 text-ink-2">Reload the page and try again.</p>
          <Button onClick={this.reset} className="mt-6">
            <RefreshCw className="size-4" aria-hidden="true" />
            Reload page
          </Button>
        </Panel>
      </Frame>
    );
  }
}
